import React, {ReactNode} from 'react';
import {Button, CheckBox, PriorityIcon, Separator} from '@priority-software/priority-style-react';
import {FieldSearchDropdownProps} from '../../types/DropdownProps';
import {ChooseOptions, SearchOptions} from '../SearchOptionsV2/SearchOptionsV2';
import {getLocalizedText, texts} from '../../../../../constants/TextConst';
import {Label} from '../../../Label';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../../utils/dataTestId';

type ChooseAdditionalProps = {
    statusRenderer?: (status) => ReactNode;
};

export const ChooseDropdownContent = (props: FieldSearchDropdownProps & ChooseAdditionalProps) => {
    return (
        <>
            <ChooseOptions {...props} />
            {props.isMulti && (
                <MultiSelectFooter onMultiSelect={props.onMultiSelect} onClose={props.onClose} />
            )}
        </>
    );
};

export const MultiSelectFooter = ({onMultiSelect, onClose}) => {
    return (
        <div
            className={styles.searchFooter}
            {...createDataTestId('input-multi-select-footer', false)}
        >
            <Button width="regularFixed" onClick={onMultiSelect}>
                {getLocalizedText(texts.ok)}
            </Button>

            <Button width="regularFixed" skin="secondary" onClick={onClose}>
                {getLocalizedText(texts.cancel)}
            </Button>
        </div>
    );
};

export type SearchDropDownHeaderProps = {
    ignorecase: boolean;
    onIgnoreCaseChange: () => void;
    searchFromBeginning: boolean;
    onSearchFromBeginningChange: () => void;
};

export const SearchDropDownHeader = (props: SearchDropDownHeaderProps) => {
    const {ignorecase, onIgnoreCaseChange, onSearchFromBeginningChange, searchFromBeginning} =
        props;

    return (
        <div className={styles.searchHeader} {...createDataTestId('input-search-header', false)}>
            <CheckBox checked={ignorecase} onChange={onIgnoreCaseChange} ariaLabel={'ignorecase'}>
                <Label skin="caption-2">{getLocalizedText(texts.ignorecase)}</Label>
            </CheckBox>

            <CheckBox
                checked={searchFromBeginning}
                onChange={onSearchFromBeginningChange}
                ariaLabel={'search-from-beggining'}
            >
                <Label skin="caption-2">{getLocalizedText(texts.searchFromBegining)}</Label>
            </CheckBox>
        </div>
    );
};

export type SearchDropDownFooterProps = {
    onZoomToNewEntry: () => void;
    onClose: () => void;
    onZoomToAdvancedSearch: () => void;
};

export const SearchDropDownFooter = ({
    onZoomToNewEntry,
    onClose,
    onZoomToAdvancedSearch
}: SearchDropDownFooterProps) => {
    return (
        <div className={styles.searchFooter} {...createDataTestId('input-search-footer', false)}>
            {onZoomToNewEntry && (
                <>
                    <Button
                        skin="text"
                        componentClasses={styles.searchLabelButton}
                        prefixIcon={PriorityIcon.roundAdd}
                        onClick={() => {
                            onClose();
                            onZoomToNewEntry();
                        }}
                    >
                        {getLocalizedText(texts.newEntry)}
                    </Button>
                    <Separator />
                </>
            )}
            <Button
                skin="text"
                componentClasses={styles.searchLabelButton}
                prefixIcon={PriorityIcon.F11}
                onClick={() => {
                    onClose();
                    onZoomToAdvancedSearch();
                }}
            >
                {getLocalizedText(texts.advancedSearch)}
            </Button>
        </div>
    );
};

export const SearchDropdownContent = (props: FieldSearchDropdownProps) => {
    const {
        results,
        ignorecase,
        onIgnoreCaseChange,
        searchFromBeginning,
        onSearchFromBeginningChange,
        onClose,
        isMulti,
        onZoomToAdvancedSearch,
        onZoomToNewEntry,
        isRelateKeyColumn,
        dropdownInputType = 'grid'
    } = props;
    return (
        <>
            <SearchDropDownHeader
                ignorecase={ignorecase}
                onIgnoreCaseChange={onIgnoreCaseChange}
                onSearchFromBeginningChange={onSearchFromBeginningChange}
                searchFromBeginning={searchFromBeginning}
            />

            <SearchOptions {...props} results={results} />

            {isMulti && <MultiSelectFooter {...props} />}

            {!isRelateKeyColumn && dropdownInputType === 'grid' && (
                <SearchDropDownFooter
                    onClose={onClose}
                    onZoomToAdvancedSearch={onZoomToAdvancedSearch}
                    onZoomToNewEntry={onZoomToNewEntry}
                />
            )}
        </>
    );
};
